import { marked } from 'marked';
import TurndownService from 'turndown';

/**
 * Markdown in, markdown out — HTML only ever exists inside the editor.
 *
 * Keeping markdown as the stored format is what lets agent drafts, version
 * history and channel adaptations stay unchanged while people edit rich text
 * (dokumentasjon/07-utviklingsplan.md 2.1.3). The round trip is therefore
 * deliberately narrow: only the constructs the toolbar can produce.
 */

const turndown = new TurndownService({
  headingStyle: 'atx',
  bulletListMarker: '-',
  codeBlockStyle: 'fenced',
  emDelimiter: '_',
  strongDelimiter: '**',
  linkStyle: 'inlined',
});

// TipTap emits an empty paragraph for a blank line; markdown has no such thing,
// and left alone it turns into a stray backslash.
turndown.addRule('emptyParagraph', {
  filter: (node) => node.nodeName === 'P' && node.textContent?.trim() === '',
  replacement: () => '',
});

// Turndown pads list markers out to four columns ("-   Punkt"). Agents and
// humans both write "- Punkt", and a diff between the two is noise in the
// version history, so we emit the ordinary single space.
turndown.addRule('listItem', {
  filter: 'li',
  replacement: (content, node) => {
    const body = content.replace(/^\n+/, '').replace(/\n+$/, '').replace(/\n/g, '\n  ');

    const parent = node.parentNode as HTMLElement | null;
    let prefix = '- ';
    if (parent?.nodeName === 'OL') {
      const start = Number(parent.getAttribute('start') ?? 1);
      const index = Array.prototype.indexOf.call(parent.children, node);
      prefix = `${start + index}. `;
    }

    return prefix + body + (node.nextSibling ? '\n' : '');
  },
});

export function markdownToHtml(markdown: string): string {
  if (!markdown.trim()) return '';
  return marked.parse(markdown, { async: false, gfm: true, breaks: false });
}

export function htmlToMarkdown(html: string): string {
  // An editor emptied by hand leaves this behind; it should read as no text.
  if (!html || html === '<p></p>') return '';
  return turndown.turndown(html).trim();
}
